import { useState } from 'react';
import {
  Badge,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import { ProjectWorkPhaseReport } from '../models/investors';
import AdminUploadFile from '../../../components/admin/AdminUploadFile';
import { FilePreviewTypes } from '../../../constants/formConstants';

type ActiveModal = 'visibility' | 'upload' | 'media' | null;

interface Props {
  reports: ProjectWorkPhaseReport[];
  loaded: boolean;
  apiErrors: string | null;
  onToggleVisibility: (reportId: string, isVisible: boolean) => Promise<void>;
  onUploadMedia: (reportId: string, formData: FormData) => Promise<void>;
  onDeleteMedia: (reportId: string, path: string) => Promise<void>;
}

const formatDate = (value: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-SV', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const WorkPhaseReportsTable = ({
  reports,
  loaded,
  apiErrors,
  onToggleVisibility,
  onUploadMedia,
  onDeleteMedia,
}: Props) => {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [selectedReport, setSelectedReport] =
    useState<ProjectWorkPhaseReport | null>(null);
  const [saving, setSaving] = useState(false);

  const openModal = (modal: ActiveModal, report: ProjectWorkPhaseReport) => {
    setSelectedReport(report);
    setActiveModal(modal);
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedReport(null);
  };

  const handleVisibilityConfirm = async () => {
    if (!selectedReport) return;
    setSaving(true);
    await onToggleVisibility(selectedReport.id, !selectedReport.is_visible);
    setSaving(false);
    closeModal();
  };

  const handleUploadFiles = async (files: any[]) => {
    if (!selectedReport || !files.length) return;
    const formData = new FormData();
    files.forEach((file) => formData.append('media', file));
    await onUploadMedia(selectedReport.id, formData);
    closeModal();
  };

  const handleDeleteMedia = async (path: string) => {
    if (!selectedReport) return;
    setSaving(true);
    await onDeleteMedia(selectedReport.id, path);
    setSaving(false);
    closeModal();
  };

  if (!loaded) return <p>Cargando...</p>;
  if (apiErrors) return <p className="text-danger">{apiErrors}</p>;
  if (reports.length === 0)
    return (
      <p className="text-muted">No hay reportes registrados para esta fase.</p>
    );

  return (
    <div
      style={{
        height: '500px',
        overflow: 'auto',
      }}
    >
      <Table className="align-items-center">
        <thead className="thead-light">
          <tr>
            <th scope="col">Título</th>
            <th scope="col">Período</th>
            <th scope="col">Publicado</th>
            <th scope="col">Visible</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => (
            <tr key={report.id}>
              <td>{report.title}</td>
              <td>{formatDate(report.report_period)}</td>
              <td>{formatDate(report.published_at)}</td>
              <td>
                <Badge color={report.is_visible ? 'success' : 'danger'}>
                  {report.is_visible ? 'Visible' : 'Oculto'}
                </Badge>
              </td>
              <td className="text-right">
                <UncontrolledDropdown positionFixed>
                  <DropdownToggle color="secondary" size="sm" caret />
                  <DropdownMenu right>
                    <DropdownItem
                      onClick={() => openModal('visibility', report)}
                    >
                      <i
                        className={`fa-solid ${report.is_visible ? 'fa-eye-slash' : 'fa-eye'} mr-2`}
                      />
                      {report.is_visible ? 'Ocultar' : 'Mostrar'}
                    </DropdownItem>
                    <DropdownItem onClick={() => openModal('upload', report)}>
                      <i className="fa-solid fa-upload mr-2" />
                      Subir imágenes
                    </DropdownItem>
                    <DropdownItem onClick={() => openModal('media', report)}>
                      <i className="fa-solid fa-images mr-2" />
                      Gestionar imágenes
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Visibility modal */}
      <Modal isOpen={activeModal === 'visibility'} toggle={closeModal}>
        <ModalHeader toggle={closeModal}>Cambiar visibilidad</ModalHeader>
        <ModalBody>
          <p>
            ¿Deseas{' '}
            <strong>
              {selectedReport?.is_visible ? 'ocultar' : 'mostrar'}
            </strong>{' '}
            el reporte <strong>"{selectedReport?.title}"</strong> en el sitio
            web?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleVisibilityConfirm}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Confirmar'}
          </Button>
          <Button color="secondary" onClick={closeModal}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Upload media modal */}
      <Modal isOpen={activeModal === 'upload'} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>Subir imágenes al reporte</ModalHeader>
        <ModalBody>
          <AdminUploadFile
            handleUpload={handleUploadFiles}
            description="Imágenes del reporte"
            filesPreviewType={FilePreviewTypes.LIST}
            dropZoneText="Formatos soportados: JPEG y PNG."
          />
        </ModalBody>
      </Modal>

      {/* Manage media modal */}
      <Modal isOpen={activeModal === 'media'} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          Gestionar imágenes del reporte
        </ModalHeader>
        <ModalBody>
          {selectedReport?.media?.length ? (
            <Row>
              {selectedReport.media.map((url) => (
                <Col key={url} md="4" className="mb-3">
                  <img
                    src={url}
                    alt="media"
                    style={{
                      width: '100%',
                      height: '120px',
                      objectFit: 'cover',
                    }}
                    className="rounded"
                  />
                  <Button
                    color="danger"
                    size="sm"
                    block
                    className="mt-1"
                    disabled={saving}
                    onClick={() => handleDeleteMedia(url)}
                  >
                    Eliminar
                  </Button>
                </Col>
              ))}
            </Row>
          ) : (
            <p className="text-muted">Este reporte no tiene imágenes.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default WorkPhaseReportsTable;
